import React, { useEffect, useRef, useState } from 'react';

export type OscilloscopeVariant = 'wave' | 'static';

interface OscilloscopeProps {
  hue?: number;
  initialWavelength?: number;
  compact?: boolean;
  variant?: OscilloscopeVariant;
  crt?: boolean;
}

export const Oscilloscope: React.FC<OscilloscopeProps> = ({
  hue = 180,
  initialWavelength = 50,
  compact = false,
  variant = 'wave',
  crt = false,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const hueRef = useRef(hue);
  const bgRef = useRef<string>('#0a0f14');

  const isStatic = variant === 'static';

  const [wavelength, setWavelength] = useState<number>(initialWavelength);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  // Sync hue ref on every render (no state, no re-render loop)
  hueRef.current = hue;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setDimensions({
          width: Math.floor(width),
          height: Math.floor(height),
        });
      }
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Canvas animation — runs continuously, reads hueRef.current each frame
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || dimensions.width === 0 || dimensions.height === 0) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Offscreen buffer used to render the undistorted static scene before the
    // fisheye pixel pass is applied to the visible canvas.
    let sourceCanvas: HTMLCanvasElement | null = null;
    let sourceCtx: CanvasRenderingContext2D | null = null;
    const getSourceCtx = () => {
      if (!sourceCanvas) {
        sourceCanvas = document.createElement('canvas');
        sourceCanvas.width = dimensions.width;
        sourceCanvas.height = dimensions.height;
        sourceCtx = sourceCanvas.getContext('2d', { willReadFrequently: true });
      }
      return { sourceCanvas, sourceCtx };
    };

    // Resolve the configurable background from CSS so it can be styled externally.
    // Re-reads each frame so live changes to --fill-high / --hue take effect.
    const container = containerRef.current;
    const resolveBg = () => {
      if (!container) return;
      const resolved = getComputedStyle(container).backgroundColor;
      if (resolved && resolved !== 'rgba(0, 0, 0, 0)' && resolved !== 'transparent') {
        bgRef.current = resolved;
      }
    };
    resolveBg();

    let animationFrameId: number;
    let phase = 0;

    const renderWave = () => {
      const { width, height } = dimensions;
      const centerY = height / 2;
      const currentHue = ((hueRef.current % 360) + 360) % 360;

      resolveBg();
      const bg = bgRef.current;

      ctx.globalAlpha = 0.3;
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, width, height);
      ctx.globalAlpha = 1;

      ctx.strokeStyle = 'rgba(255, 255, 255, 0.05)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let x = 0; x < width; x += 40) {
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
      }
      for (let y = 0; y < height; y += 40) {
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
      }
      ctx.stroke();

      const hueFactor = currentHue / 360;
      const freq = 0.01 + hueFactor * 0.06;
      const amp = height * (0.15 + hueFactor * 0.2);

      const color = `hsl(${currentHue}, 100%, 70%)`;
      const glowColor = `hsl(${currentHue}, 100%, 70%)`;

      ctx.shadowBlur = 8 + hueFactor * 16;
      ctx.shadowColor = glowColor;
      ctx.strokeStyle = color;
      ctx.lineWidth = 4 + hueFactor * 2;

      ctx.beginPath();
      for (let x = 0; x < width; x++) {
        const y = centerY + Math.sin(x * freq + phase) * amp;
        if (x === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();

      ctx.shadowBlur = 0;
      phase -= 0.06 + hueFactor * 0.06;

      animationFrameId = requestAnimationFrame(renderWave);
    };

    const DISTORTION = 1.8;
    const distortionMinusOne = DISTORTION - 1;

    const renderStatic = () => {
      const { width, height } = dimensions;
      const currentHue = ((hueRef.current % 360) + 360) % 360;
      const hueRad = (currentHue * Math.PI) / 180;

      resolveBg();
      const bg = bgRef.current;

      const { sourceCanvas: srcCanvas, sourceCtx: srcCtx } = getSourceCtx();
      const drawCtx = srcCtx && srcCanvas ? srcCtx : ctx;

      drawCtx.fillStyle = bg;
      drawCtx.fillRect(0, 0, width, height);

      const noise = drawCtx.createImageData(width, height);
      const data = noise.data;

      for (let i = 0; i < data.length; i += 4) {
        const grey = Math.random() * 255;
        data[i] = grey;
        data[i + 1] = grey;
        data[i + 2] = grey;
        data[i + 3] = 60 + Math.random() * 195;
      }
      drawCtx.putImageData(noise, 0, 0);

      drawCtx.fillStyle = `hsla(${currentHue}, 100%, 60%, 0.15)`;
      drawCtx.fillRect(0, 0, width, height);

      // Rolling horizontal band
      const bandY = (phase * 60) % (height + 200) - 100;
      const bandGrad = drawCtx.createLinearGradient(0, bandY - 40, 0, bandY + 40);
      bandGrad.addColorStop(0, `hsla(${currentHue}, 80%, 60%, 0)`);
      bandGrad.addColorStop(0.5, `hsla(${currentHue}, 80%, 70%, 0.18)`);
      bandGrad.addColorStop(1, `hsla(${currentHue}, 80%, 60%, 0)`);
      drawCtx.fillStyle = bandGrad;
      drawCtx.fillRect(0, bandY - 40, width, 80);

      if (srcCtx && srcCanvas) {
        const srcImageData = srcCtx.getImageData(0, 0, width, height);
        const destImageData = ctx.createImageData(width, height);

        const srcPixels = srcImageData.data;
        const destPixels = destImageData.data;

        const cx = width / 2;
        const cy = height / 2;
        const lensRadius = Math.hypot(cx, cy) || 1;

        for (let y = 0; y < height; y++) {
          for (let x = 0; x < width; x++) {
            const dx = x - cx;
            const dy = y - cy;
            const distance = Math.sqrt(dx * dx + dy * dy);

            let srcX = x;
            let srcY = y;

            if (distance < lensRadius) {
              const normDist = distance / lensRadius;
              const formula = Math.pow(normDist, distortionMinusOne);
              srcX = Math.floor(cx + dx * formula);
              srcY = Math.floor(cy + dy * formula);
              srcX = Math.max(0, Math.min(width - 1, srcX));
              srcY = Math.max(0, Math.min(height - 1, srcY));
            }

            const destIdx = (y * width + x) * 4;
            const srcIdx = (srcY * width + srcX) * 4;

            destPixels[destIdx] = srcPixels[srcIdx];
            destPixels[destIdx + 1] = srcPixels[srcIdx + 1];
            destPixels[destIdx + 2] = srcPixels[srcIdx + 2];
            destPixels[destIdx + 3] = srcPixels[srcIdx + 3];
          }
        }

        ctx.putImageData(destImageData, 0, 0);
      }

      phase += 0.004 + Math.sin(hueRad) * 0.002;

      animationFrameId = requestAnimationFrame(renderStatic);
    };

    if (isStatic) renderStatic();
    else renderWave();

    return () => cancelAnimationFrame(animationFrameId);
  }, [dimensions, isStatic]);

  const containerStyle: React.CSSProperties = {
    position: 'relative',
    width: '100%',
    height: '100%',
    minHeight: compact ? 0 : '200px',
    overflow: 'hidden',
    background: 'var(--oscilloscope-bg, var(--fill-lowest))',
    borderRadius: compact ? 0 : '8px',
    boxSizing: 'border-box',
  };

  if (crt) {
    containerStyle.boxShadow = 'inset 0 0 60px rgba(0, 0, 0, 0.85)';
  }

  return (
    <div ref={containerRef} style={containerStyle}>
      {(crt || isStatic) && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            backgroundImage:
              'repeating-linear-gradient(0deg, rgba(0,0,0,0.22) 0px, rgba(0,0,0,0.22) 1px, transparent 1px, transparent 3px)',
            zIndex: 1,
          }}
        />
      )}
      <canvas
        ref={canvasRef}
        width={dimensions.width}
        height={dimensions.height}
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
        }}
      />

      {!compact && (
        <div
          style={{
            position: 'absolute',
            bottom: '12px',
            left: '12px',
            right: '12px',
            display: 'flex',
            gap: '16px',
            backdropFilter: 'blur(8px)',
            padding: '8px 16px',
            borderRadius: '6px',
            color: '#fff',
            fontFamily: 'monospace',
            fontSize: '12px',
            border: `1px solid hsl(${hueRef.current}, 40%, 20%)`,
            zIndex: 2,
          }}
        >
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
            <span>λ: {wavelength}px</span>
            <input
              type="range"
              min="15"
              max="200"
              value={wavelength}
              onChange={(e) => setWavelength(Number(e.target.value))}
              style={{ width: '100%' }}
            />
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
            <span>Hue: {hueRef.current}°</span>
            <input
              type="range"
              min="0"
              max="360"
              value={hueRef.current}
              onChange={(e) => { /* hue is controlled externally */ }}
              style={{ width: '100%' }}
            />
          </label>
        </div>
      )}
    </div>
  );
};
